import { effect } from "@preact/signals";
import Phaser from "phaser";
import { tid } from "@/assets/tileAtlas";
import { battle, type BattleState } from "@/battle/BattleController";
import { TILE, VIEW_H, VIEW_W } from "@/config";
import { avatarDef } from "@/content/heroes";
import { createRng } from "@/math";
import { hero } from "@/state/store";

/** Draws the battle stage (meadow backdrop, sprites, HP bars). The question panel itself is HTML on top. */
export class BattleScene extends Phaser.Scene {
  private monster!: Phaser.GameObjects.Sprite;
  private heroSprite!: Phaser.GameObjects.Sprite;
  private bars!: Phaser.GameObjects.Graphics;
  private lastMonsterFlash = 0;
  private lastHeroFlash = 0;
  private finished = false;

  constructor() {
    super("Battle");
  }

  create(): void {
    this.finished = false;
    this.buildBackdrop();

    const s = battle.value;
    const texture = s ? s.monster.texture : "mon-slime";
    const monsterX = VIEW_W * 0.7;
    const monsterY = VIEW_H * 0.42;
    const heroX = VIEW_W * 0.28;
    const heroY = VIEW_H * 0.58;

    const shadows = this.add.graphics().setDepth(5);
    shadows.fillStyle(0x1b3a1b, 0.35);
    shadows.fillEllipse(monsterX, monsterY + 30, 92, 22);
    shadows.fillEllipse(heroX, heroY + 22, 60, 16);

    const scale = s?.monster.rank === "boss" ? 5.5 : 4;
    this.monster = this.add.sprite(monsterX, monsterY, texture, 0).setScale(scale).setDepth(6);
    this.monster.play(`${texture}-idle`);
    this.tweens.add({
      targets: this.monster,
      y: monsterY - 5,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: "Sine.inOut",
    });

    // hero seen from behind (frame 4 = first frame of the "up" row)
    this.heroSprite = this.add
      .sprite(heroX, heroY, avatarDef(hero.value.avatar).texture, 4)
      .setScale(3)
      .setDepth(6);

    this.bars = this.add.graphics().setDepth(7);

    const dispose = effect(() => {
      const b = battle.value;
      if (!b) {
        this.finish();
        return;
      }
      this.draw(b);
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => dispose());
  }

  /** A meadow clearing built from the same tiles as the world, with a dense tree line at the top. */
  private buildBackdrop(): void {
    const cols = Math.ceil(VIEW_W / TILE);
    const rows = Math.ceil(VIEW_H / TILE);
    const rng = createRng(4242);
    const grass = [tid("grass"), tid("grass"), tid("grass"), tid("grass_b"), tid("grass_c"), tid("grass_d")];
    const flowers = ["flower_yellow", "flower_white", "flower_red", "tuft_a", "tuft_b", "flower_blue"].map(
      tid,
    );
    const base: number[][] = [];
    const decor: number[][] = [];
    const objects: number[][] = [];
    for (let y = 0; y < rows; y++) {
      base.push(Array.from({ length: cols }, () => rng.pick(grass)));
      decor.push(Array.from({ length: cols }, () => (y > 3 && rng.chance(0.08) ? rng.pick(flowers) : -1)));
      objects.push(Array.from({ length: cols }, () => -1));
    }
    for (const [x, y, id] of [
      [1, 6, "bush"],
      [cols - 2, 7, "bush_orange"],
      [2, 12, "rock_small"],
      [cols - 3, 13, "bush"],
      [cols - 2, 4, "rock_small_orange"],
    ] as Array<[number, number, string]>) {
      (objects[y] as number[])[x] = tid(id);
    }

    const map = this.make.tilemap({ data: base, tileWidth: TILE, tileHeight: TILE });
    const tiles = map.addTilesetImage("tiles", "tiles", TILE, TILE, 0, 0);
    if (!tiles) return;
    map.createLayer(0, tiles, 0, 0)?.setDepth(0);
    const put = (name: string, data: number[][]) => {
      const layer = map.createBlankLayer(name, tiles);
      data.forEach((row, y) => row.forEach((v, x) => v >= 0 && layer?.putTileAt(v, x, y)));
      layer?.setDepth(1);
    };
    put("decor", decor);
    put("objects", objects);

    // staggered tree wall along the top edge, like the world's forest borders
    const kinds = ["tree_a", "tree_a", "tree_b", "tree_c"];
    for (let px = 4; px < VIEW_W + 16; px += 22) {
      this.add.image(px, 30, "tileset-nature", rng.pick(kinds)).setOrigin(0.5, 1).setDepth(2);
      this.add
        .image(px + 11, 46, "tileset-nature", rng.pick(kinds))
        .setOrigin(0.5, 1)
        .setDepth(3);
    }
    this.add.image(60, 70, "tileset-nature", "tree_big_b").setOrigin(0.5, 1).setDepth(4);
    this.add
      .image(VIEW_W - 50, 66, "tileset-nature", "tree_big_a")
      .setOrigin(0.5, 1)
      .setDepth(4);

    // soft vignette so the sprites and the HTML panel read well
    const shade = this.add.graphics().setDepth(5);
    shade.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.45, 0.45);
    shade.fillRect(0, VIEW_H * 0.55, VIEW_W, VIEW_H * 0.45);
  }

  private draw(b: BattleState): void {
    this.bars.clear();
    this.drawBar(VIEW_W * 0.7 - 40, VIEW_H * 0.14, 80, 6, b.monsterHp / b.monster.hp, 0xe74c3c);
    this.drawBar(VIEW_W * 0.28 - 40, VIEW_H * 0.3, 80, 6, b.heroHp / b.heroHpMax, 0x2ecc71);

    if (b.monsterFlash !== this.lastMonsterFlash) {
      this.lastMonsterFlash = b.monsterFlash;
      this.monster.setTintFill(0xffffff);
      this.tweens.add({ targets: this.monster, x: this.monster.x + 8, duration: 60, yoyo: true, repeat: 2 });
      this.time.delayedCall(120, () => this.monster.clearTint());
      this.cameras.main.flash(120, 255, 255, 255, false);
    }
    if (b.heroFlash !== this.lastHeroFlash) {
      this.lastHeroFlash = b.heroFlash;
      this.heroSprite.setTintFill(0xff6b6b);
      this.time.delayedCall(150, () => this.heroSprite.clearTint());
      this.cameras.main.shake(180, 0.01);
    }
    if (b.phase === "won") {
      this.tweens.add({ targets: this.monster, alpha: 0, angle: 90, y: this.monster.y + 30, duration: 500 });
    }
    if (b.phase === "lost") {
      this.tweens.add({ targets: this.heroSprite, alpha: 0.3, angle: -90, duration: 500 });
    }
  }

  private drawBar(x: number, y: number, w: number, h: number, ratio: number, color: number): void {
    const r = Phaser.Math.Clamp(ratio, 0, 1);
    this.bars.fillStyle(0x000000, 0.6);
    this.bars.fillRect(x - 1, y - 1, w + 2, h + 2);
    this.bars.fillStyle(0x444444, 1);
    this.bars.fillRect(x, y, w, h);
    this.bars.fillStyle(color, 1);
    this.bars.fillRect(x, y, Math.round(w * r), h);
  }

  private finish(): void {
    if (this.finished) return;
    this.finished = true;
    const world = this.scene.get("World");
    this.scene.stop();
    this.scene.resume("World");
    world.events.emit("battle-ended");
  }
}
